/* ============================================================
   The school's half of a Trasse Families link.

   A household has asked to connect a child to this school. Only the school can
   answer — that check runs server-side, from the caller's own org, because a
   family accepting its own request would put a child on this roster without
   anyone here agreeing.

   Deliberately self-contained and mounted inside Accounts rather than given its
   own tab: a school with no linked families should not grow a permanently empty
   room in its console, so this renders NOTHING at all until a household
   actually asks. It also keeps the change to BusTracking.tsx to a single line.

   Styled with the school console's own Tailwind classes, not the violet
   Families palette — this is a school screen, and a parent-branded block
   dropped into it would read as somebody else's product.
   ============================================================ */
import React, { useCallback, useEffect, useState } from 'react';
import {
  FamilySchoolLink, listSchoolLinks, respondToSchoolLink,
} from '../services/paymentsApi';

interface Props {
  /** The school's own org code. */
  orgCode: string | null;
}

export default function SchoolFamilyLinksPanel({ orgCode }: Props) {
  const [links, setLinks] = useState<FamilySchoolLink[]>([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!orgCode) return;
    const res = await listSchoolLinks(orgCode);
    if (res.ok) setLinks(res.links);
  }, [orgCode]);

  useEffect(() => { load(); }, [load]);

  // Only rows where THIS org is the school. The endpoint returns both sides for
  // an org, and a school is never the family side — but filtering here means a
  // future shape change cannot quietly show a school its own outbound requests.
  const mine = links.filter((l) => l.schoolCode === orgCode);
  const pending = mine.filter((l) => l.status === 'requested');
  const connected = mine.filter((l) => l.status === 'accepted');

  const act = async (linkId: string, action: 'accept' | 'decline' | 'revoke') => {
    setErr(''); setBusy(linkId);
    const res = await respondToSchoolLink(linkId, action);
    setBusy('');
    if (!res.ok) { setErr(res.message || 'That did not go through.'); return; }
    load();
  };

  // Nothing has ever been asked: stay silent rather than adding an empty box to
  // a console that is already dense.
  if (!orgCode || (pending.length === 0 && connected.length === 0)) return null;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Trasse Families</h2>
        <p className="text-sm text-gray-600">
          Households that run their own car and want their child on your school trips. Accepting lets
          you add that child to a trip without an invite code. You are not taking on their vehicle,
          their driver or their subscription.
        </p>
      </div>

      {err && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
      )}

      {pending.length > 0 && (
        <div className="space-y-2">
          {pending.map((l) => (
            <div
              key={l.linkId}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {l.familyName || l.familyOrgCode}
                </p>
                <p className="text-xs text-gray-600">
                  wants to connect
                  {l.childIds.length
                    ? ` · ${l.childIds.length} child${l.childIds.length === 1 ? '' : 'ren'}`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                disabled={busy === l.linkId}
                onClick={() => act(l.linkId, 'accept')}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy === l.linkId ? 'Saving…' : 'Accept'}
              </button>
              <button
                type="button"
                disabled={busy === l.linkId}
                onClick={() => act(l.linkId, 'decline')}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50"
              >
                Decline
              </button>
            </div>
          ))}
        </div>
      )}

      {connected.length > 0 && (
        <div className="space-y-2">
          {connected.map((l) => (
            <div
              key={l.linkId}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {l.familyName || l.familyOrgCode}
                </p>
                <p className="text-xs text-emerald-700">Connected</p>
              </div>
              <button
                type="button"
                disabled={busy === l.linkId}
                onClick={() => act(l.linkId, 'revoke')}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-50"
              >
                Disconnect
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
